import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  createEventType,
  fetchEventTypes,
  setEventTypeActive,
  updateEventType,
  type EventType,
} from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';

type FormatValue = '' | 'success' | 'director';

const FORMAT_OPTIONS: { value: FormatValue; label: string }[] = [
  { value: '', label: '通常のイベント' },
  { value: 'success', label: '1対1の面談（相手はサクセス担当）' },
  { value: 'director', label: '1対1の面談（相手は理事・講師）' },
];

function formatLabel(type: EventType): string {
  return FORMAT_OPTIONS.find((o) => o.value === (type.counterpart_role ?? ''))?.label ?? '通常';
}

/**
 * 業務マスター > イベント種別（指示書 §4・§10）。
 * 種別が持つのは「名称」と「形式（通常／1対1の面談）」だけ。
 * 出欠を取るか・交流会があるかは、種別ではなくイベントごとに決める（D-127）。
 */
export function EventTypesPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['event-types'], queryFn: fetchEventTypes });

  const [newName, setNewName] = useState('');
  const [newFormat, setNewFormat] = useState<FormatValue>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingFormat, setEditingFormat] = useState<FormatValue>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createEventType({ name: newName, counterpart_role: newFormat || null });
      setNewName('');
      setNewFormat('');
      await queryClient.invalidateQueries({ queryKey: ['event-types'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  function startEdit(type: EventType): void {
    setEditingId(type.id);
    setEditingName(type.name);
    setEditingFormat((type.counterpart_role ?? '') as FormatValue);
    setError(null);
  }

  async function onSaveEdit(): Promise<void> {
    if (editingId === null) return;
    setBusy(true);
    setError(null);
    try {
      await updateEventType(editingId, {
        name: editingName,
        counterpart_role: editingFormat || null,
      });
      setEditingId(null);
      await queryClient.invalidateQueries({ queryKey: ['event-types'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleActive(type: EventType): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setEventTypeActive(type.id, !type.is_active);
      await queryClient.invalidateQueries({ queryKey: ['event-types'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<EventType>[] = [
    { key: 'code', header: 'コード', render: (t) => t.code },
    { key: 'name', header: '名称', primary: true, render: (t) => t.name },
    { key: 'format', header: '形式', render: (t) => formatLabel(t) },
    { key: 'event_count', header: '使用中件数', render: (t) => `${t.event_count}件` },
    { key: 'is_active', header: '状態', render: (t) => (t.is_active ? '有効' : '無効化') },
  ];

  return (
    <>
      <PageHeader
        title="イベント種別"
        description="月例会・分科会・サクセス面談・1on1など、開催するイベントの種別を登録します。ここに登録すると、イベント作成の「種別」の選択肢になり、通知プリセットの適用対象としても選べます。コードは登録時に自動で採番されます。出欠を取るかは、イベントを作るときに1件ずつ決めます。"
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">形式</span>
            <select
              value={newFormat}
              onChange={(e) => setNewFormat(e.target.value as FormatValue)}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              {FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            種別を追加
          </button>
        </form>
      </div>

      {/* 一覧の行を押した結果はモーダルで出す（D-094）。一覧の上下にインライン表示すると
          気付きにくい（実機フィードバック / D-164） */}
      <Modal
        open={editingId !== null}
        title="種別を編集"
        onClose={() => setEditingId(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="btn-secondary"
            >
              やめる
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSaveEdit()}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              保存
            </button>
          </>
        }
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void onSaveEdit();
          }}
        >
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">名称</span>
            <input
              required
              autoFocus
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-wptext-muted">形式</span>
            <select
              value={editingFormat}
              onChange={(e) => setEditingFormat(e.target.value as FormatValue)}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              {FORMAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Modal>

      <ListTable<EventType>
        caption="イベント種別一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(t) => t.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          { label: '編集', onSelect: (t: EventType) => startEdit(t) },
          { label: '無効化・有効化', onSelect: (t: EventType) => void onToggleActive(t) },
        ]}
        emptyTitle="イベント種別が登録されていません"
      />
    </>
  );
}
